import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Zap, Receipt, LogOut, User, BarChart3, Bell, Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSlip } from "../context/SlipContext";
import { useWatch } from "../context/WatchContext";
import { AlertsPanel } from "./AlertsPanel";
import { Button } from "./ui/button";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { legs, setOpen } = useSlip();
  const { unread } = useWatch();
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  const navItem = (to, label) => (
    <Link
      to={to}
      data-testid={`nav-${label.toLowerCase()}`}
      className={`text-sm font-medium transition-colors hover:text-white ${
        loc.pathname === to ? "text-white" : "text-slate-400"
      }`}
    >
      {label}
    </Link>
  );

  const mobileItem = (to, label) => (
    <Link
      to={to}
      data-testid={`mobile-nav-${label.toLowerCase()}`}
      onClick={() => setMenuOpen(false)}
      className={`rounded-lg px-3 py-3 text-sm font-semibold transition-colors ${
        loc.pathname === to ? "bg-[#1B273E] text-white" : "text-slate-300 hover:bg-[#141C2B]"
      }`}
    >
      {label}
    </Link>
  );

  return (
    <header className="sticky top-0 z-40 glass border-b border-[#233248]">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 h-16 flex items-center justify-between gap-2 sm:gap-4">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 shrink-0">
          <div className="h-9 w-9 rounded-xl bg-[#00E5FF] grid place-items-center cyan-glow">
            <Zap className="h-5 w-5 text-[#0B0F17]" strokeWidth={2.5} />
          </div>
          <div className="leading-none">
            <div className="font-heading font-extrabold text-white text-base sm:text-lg tracking-tight">
              Aurum<span className="text-[#00E5FF]">PropsAI</span>
            </div>
            <div className="hidden sm:block font-data text-[10px] text-slate-500 tracking-widest">AI ENGINE</div>
          </div>
        </Link>

        <nav className="hidden sm:flex items-center gap-6 ml-4">
          {navItem("/", "Dashboard")}
          {navItem("/rankings", "Rankings")}
          {navItem("/results", "Results")}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2 ml-auto">
          <button
            data-testid="alerts-bell-button"
            onClick={() => setAlertsOpen(true)}
            className="relative rounded-lg border border-[#233248] bg-[#141C2B] hover:bg-[#1B273E] h-10 w-10 grid place-items-center text-white"
          >
            <Bell className="h-4 w-4" />
            {unread > 0 && (
              <span
                data-testid="alerts-unread-badge"
                className="absolute -top-2 -right-2 h-5 min-w-5 px-1 rounded-full bg-[#F59E0B] text-[#0B0F17] text-xs font-bold grid place-items-center font-data"
              >
                {unread}
              </span>
            )}
          </button>
          <Button
            data-testid="bet-slip-toggle-button"
            variant="outline"
            onClick={() => setOpen(true)}
            className="relative border-[#233248] bg-[#141C2B] hover:bg-[#1B273E] text-white gap-2"
          >
            <Receipt className="h-4 w-4" />
            <span className="hidden sm:inline">Slip</span>
            {legs.length > 0 && (
              <span
                data-testid="slip-count-badge"
                className="absolute -top-2 -right-2 h-5 min-w-5 px-1 rounded-full bg-[#00E5FF] text-[#0B0F17] text-xs font-bold grid place-items-center font-data"
              >
                {legs.length}
              </span>
            )}
          </Button>

          {user ? (
            <div className="flex items-center gap-2">
              <span className="hidden md:flex items-center gap-1.5 text-sm text-slate-300">
                <User className="h-4 w-4 text-[#00E5FF]" />
                {user.name?.split(" ")[0] || "You"}
              </span>
              <Button
                data-testid="logout-button"
                variant="ghost"
                size="icon"
                onClick={logout}
                className="text-slate-400 hover:text-white hover:bg-[#1B273E]"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button
              data-testid="auth-signin-button"
              onClick={() => nav("/login")}
              className="bg-[#00E5FF] text-[#0B0F17] hover:bg-[#33EaFF] font-semibold px-3 sm:px-4"
            >
              Sign In
            </Button>
          )}

          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
            className="sm:hidden rounded-lg border border-[#233248] bg-[#141C2B] hover:bg-[#1B273E] h-10 w-10 grid place-items-center text-white"
          >
            {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav
          data-testid="mobile-nav"
          className="sm:hidden border-t border-[#233248] glass px-3 py-2 flex flex-col gap-1"
        >
          {mobileItem("/", "Dashboard")}
          {mobileItem("/rankings", "Rankings")}
          {mobileItem("/results", "Results")}
        </nav>
      )}
      <AlertsPanel open={alertsOpen} onOpenChange={setAlertsOpen} />
    </header>
  );
};
